import { useState } from "react";

import { useAddresses, useCreateAddress, useDeleteAddress } from "../api";
import { readApiErrorMessage } from "../errors";
import type { AddressRow } from "../types";

// Phase 4a customer-portal FR-9 (Addresses tab). Add / list /
// delete; one designated default per the existing `default_delivery`
// column (an admin uniqueness guard already enforces "at most one
// default" — we just toggle here).
//
// FR-11 — site-address edits on an active order go through admin,
// not here; this tab manages the customer's profile addresses only.
export function AddressesTab(): JSX.Element {
  const q = useAddresses();
  const create = useCreateAddress();
  const del = useDeleteAddress();
  const [show, setShow] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (q.isLoading) return <div className="text-sm text-zinc-600">Loading…</div>;
  if (!q.data) return <div className="text-sm text-red-700">Couldn&rsquo;t load addresses.</div>;

  function handleDelete(id: number): void {
    setErr(null);
    del.mutate(id, { onError: (e) => setErr(readApiErrorMessage(e)) });
  }

  function handleCreate(input: Pick<AddressRow, "line1" | "line2" | "city" | "state" | "postal_code" | "label">): void {
    setErr(null);
    create.mutate(input, {
      onSuccess: () => setShow(false),
      onError: (e) => setErr(readApiErrorMessage(e))
    });
  }

  return (
    <div className="space-y-3 text-sm">
      <ul className="space-y-2">
        {q.data.rows.map((a) => (
          <li key={a.id} className="flex items-start justify-between rounded border border-zinc-200 p-2">
            <div>
              <div className="font-medium">{a.label ?? "Address"}</div>
              <div className="text-xs text-zinc-700">
                {a.line1}
                {a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} {a.postal_code}
              </div>
              {a.default_delivery ? <span className="text-xs text-green-700">default</span> : null}
            </div>
            <button
              type="button"
              onClick={() => handleDelete(a.id)}
              disabled={del.isPending}
              className="text-xs underline disabled:opacity-40"
              data-testid={`delete-address-${a.id}`}
            >
              Remove
            </button>
          </li>
        ))}
        {q.data.rows.length === 0 ? <li className="text-xs text-zinc-500">No addresses saved.</li> : null}
      </ul>

      {err ? (
        <div className="rounded border border-red-600 bg-red-50 p-2 text-sm" data-testid="address-error">
          {err}
        </div>
      ) : null}

      {show ? (
        <NewAddressForm onSave={handleCreate} pending={create.isPending} />
      ) : (
        <button
          type="button"
          onClick={() => setShow(true)}
          className="rounded border border-zinc-300 px-3 py-2 text-sm"
          data-testid="add-address"
        >
          + Add address
        </button>
      )}
    </div>
  );
}

interface NewProps {
  onSave: (input: Pick<AddressRow, "line1" | "line2" | "city" | "state" | "postal_code" | "label">) => void;
  pending: boolean;
}

// eslint-disable-next-line max-lines-per-function
function NewAddressForm({ onSave, pending }: NewProps): JSX.Element {
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postal, setPostal] = useState("");
  const [label, setLabel] = useState("");
  return (
    <div className="space-y-2 rounded border border-zinc-300 p-2">
      <Input value={label} onChange={setLabel} placeholder="Label (Home / Site / etc.)" testid="addr-label" />
      <Input value={line1} onChange={setLine1} placeholder="Street address" testid="addr-line1" />
      <Input value={line2} onChange={setLine2} placeholder="Apt / Suite (optional)" testid="addr-line2" />
      <div className="flex gap-2">
        <Input value={city} onChange={setCity} placeholder="City" testid="addr-city" />
        <Input value={state} onChange={setState} placeholder="ST" testid="addr-state" />
        <Input value={postal} onChange={setPostal} placeholder="ZIP" testid="addr-postal" />
      </div>
      <button
        type="button"
        onClick={() => onSave({ line1, line2, city, state, postal_code: postal, label })}
        disabled={pending}
        className="rounded bg-zinc-900 px-3 py-2 text-sm text-white disabled:opacity-40"
        data-testid="addr-save"
      >
        {pending ? "Saving…" : "Save address"}
      </button>
    </div>
  );
}

function Input({
  value,
  onChange,
  placeholder,
  testid
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  testid: string;
}): JSX.Element {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="min-h-[44px] w-full rounded border px-3 py-2"
      data-testid={testid}
    />
  );
}
